import React, { createContext, useContext, useState, useRef } from 'react';
import VideoPlayerCompact from './components/VideoPlayerCompact';
import {getUploadedImagePath, notifyError} from '@nutastic/nutastic-react-app';

const STREAMER_MODES = {
  VIDEO: 'VIDEO',
  PLAYLIST: 'PLAYLIST',
  BROADCAST: 'BROADCAST'
};

/**
 * @typedef NuStreamerVideo
 * @property {string} title - name of track
 * @property {string} src - file source, ID, or URL for video
 * @property {string} type - video type
 * @property {string?} img - cover image
 * @property {string?} thumbnail - small cover image
 */

/**
 * @typedef NuStreamerContextValues
 * @property {{videoId: string, config: Object}} activeVideo - active video information that is playing in deck
 * @property {NuStreamerVideo[]} videoList - list of videos in playlist deck
 * @property {function} setVideoList - set the array of videos in the deck
 * @property {Number} videoListIndex - active video in the playlist
 * @property {function} setVideoListIndex - change index for video playlist
 * @property {number} timeProgress - progress timer
 * @property {function(number)} setTimeProgress - change progress timer
 * @property {number} duration - video duration in seconds
 * @property {function(number)} setDuration - change duration time (in seconds)
 * @property {number} isSeeking - true if video is being seeked
 * @property {function(boolean)} setIsSeeking - tell the parent if you are looking or not
 * @property {boolean} isPlayingVideo - true if video is playing
 * @property {function(boolean)} setIsPlayingVideo - change video player state
 * @property {function(boolean)} setShowCompactVideo - toggle mini player on or off
 * @property {boolean} showCompactVideo - true if mini player is active
 * @property {function(videoId: string, videoSource: string, config: Object)} startVideoPlayer - use this when starting to play a new video
 * @property {function()} stopVideoPlayer - use this when stopping a video
 * @property {function} onUpdateMediaSession - updates media session with active video or provided information
 * @property {{PLAYLIST: string, VIDEO: string, BROADCAST: string}} STREAMER_MODES - streamer mode constants
 * @property {function(string)} setStreamerMode - change mode of streamer
 * @property {string} streamerMode - current streamer mode
 * @property {function(broadcastId: string, videoRef: any)} onLoadBroadcast - function to call when loading playing a broadcast again (if needed)
 */

/**
 *
 * @type NuStreamerContextValues
 */
const defaultValue = {};
const NuStreamerContext = createContext(defaultValue);
const defaultVideoState = { videoId: null, config: null };

/**
 * Streamer provider that gives details about active video being played in app. Does not control video players
 * @param children
 * @param initialPlaylist
 * @returns {JSX.Element}
 * @constructor
 */
const NuStreamerProvider = ({ children, onLoadBroadcast, onStreamProgress }) => {
  const [streamerMode, setStreamerMode] = useState(STREAMER_MODES.VIDEO);
  const [activeVideo, setActiveVideo] = useState({...defaultVideoState});
  const [videoList, setVideoList] = useState(null);
  const [videoListIndex, setVideoListIndex] = useState(null);
  const [timeProgress, setTimeProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [showCompactVideo, setShowCompactVideo] = useState(false);

  const onUpdateMediaSession = (config) => {
    const configToUse = config || activeVideo?.config;
    if(configToUse && configToUse.hasOwnProperty('title') && configToUse.hasOwnProperty('image')) {
      const { title, contentArtist, image } = configToUse;
      const artistNames = [];
      (contentArtist || []).forEach(a => artistNames.push(a?.name));

      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: title,
          artist: artistNames.join(", "),
          album: 'NuStation.net',
          artwork: [
            { src: getUploadedImagePath(image) }
          ]
        });
      }
    }
  };

  const stopVideoPlayer = () => {
    setIsPlayingVideo(false);
    setShowCompactVideo(false);
    setActiveVideo({ ...defaultVideoState });
    setDuration(0);
    setTimeProgress(0);
    setStreamerMode(STREAMER_MODES.VIDEO);
    setVideoList(null);
    setVideoListIndex(null);
  };

  const startVideoPlayer = (videoId, videoSource, config) => {
    if(config && videoSource) {
      if (isPlayingVideo) stopVideoPlayer();
      setActiveVideo({ videoId, videoSource, config });
      setIsPlayingVideo(true);
      onUpdateMediaSession(config);
    } else {
      console.error('Unable to start video without a config and source', videoId);
    }
  };

  const _onLoadBroadcastClicked = async (broadcastId, videoRef) => {
    const onComplete = ({ broadcastSchedule, targetTrackIndex, trackLandingTime, targetVideoSrc }) => {
      stopVideoPlayer();
      setStreamerMode(STREAMER_MODES.BROADCAST);
      setVideoList(broadcastSchedule);
      setVideoListIndex(targetTrackIndex);
      startVideoPlayer(broadcastId, targetVideoSrc, broadcastSchedule[targetTrackIndex]);
      setTimeout(() => {
        videoRef.current.play();
        videoRef.current.someoneIsSeeking = true;
        videoRef.current.currentTime = trackLandingTime;
      }, 1500);
    }
    const onFail = message => {
      notifyError('loadBroadcastError', 'Uh Oh!', message);
    }
    await onLoadBroadcast({ broadcastId: broadcastId }, onComplete, onFail);
  };

  const _onSetTimeProgress = v => {
    setTimeProgress(v);
    const shouldSendUpdate = Math.floor(v) % 10 === 0;
    if (shouldSendUpdate) {
      onStreamProgress(Math.floor(v), { ...activeVideo, duration })
    }
  };

  const contextValues = {
    videoList,
    setVideoList,
    videoListIndex,
    setVideoListIndex,
    duration,
    setDuration,
    timeProgress,
    setTimeProgress: _onSetTimeProgress,
    activeVideo,
    setActiveVideo,
    isPlayingVideo,
    setIsPlayingVideo,
    setShowCompactVideo,
    showCompactVideo,
    startVideoPlayer,
    stopVideoPlayer,
    onUpdateMediaSession,
    STREAMER_MODES,
    streamerMode,
    setStreamerMode,
    isSeeking,
    setIsSeeking,
    onLoadBroadcast: _onLoadBroadcastClicked
  };

  return (
    <NuStreamerContext.Provider value={contextValues}>
      {children}
      { showCompactVideo && <VideoPlayerCompact /> }
    </NuStreamerContext.Provider>
  );
};

/**
 *
 * @returns {NuStreamerContextValues}
 */
const useNuStreamerContext = () => {
  const context = useContext(NuStreamerContext);
  if (context === undefined) {
    throw new Error(
      'useNuStreamerContext must be used within an NuStreamerProvider'
    );
  }
  return context;
};

export {
  NuStreamerProvider,
  useNuStreamerContext
};
